import { createReducer } from '../../../helpers/reducer';
import actionTypes from './actionTypes';
import { ChecklistsState, ILoadChecklistsSuccessPayload } from './types';
import {
  CreateChecklistSuccessAction,
  UpdateChecklistSuccessAction,
  LoadChecklistSuccessAction,
  LoadFormDataSuccessAction,
  LoadPositionsSuccessAction,
  LoadUnitsSuccessAction,
  ValidateUnitsSuccessAction,
} from './actions.d';

const initState: ChecklistsState = {
  checklists: [],
  isLoading: true,
  isDeleting: false,
  total: 0,
  filteredTotal: 0,
  filters: [],
  sortableColumns: [],
  isCreating: false,
  createdChecklist: null,
  isUpdating: false,
  updatedChecklist: null,
  loadedChecklist: null,
  isChecklistLoading: false,
  isChecklistLoaded: false,
  formData: {
    legalEntities: [],
    predefinedTasks: [],
  },
  isFormDataLoading: false,
  isFormDataLoaded: false,
  positions: [],
  isPositionsLoading: false,
  isPositionsLoaded: false,
  units: [],
  isUnitsLoading: false,
  isUnitsLoaded: false,
  invalidUnitIds: null,
};

function onLoadChecklists(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isLoading: true,
  };
}

function onLoadChecklistsSuccess(
  state: ChecklistsState,
  { payload }: { payload: ILoadChecklistsSuccessPayload },
): ChecklistsState {
  const { records, totalCount, filteredCount, filters, sortableColumns } = payload;

  return {
    ...state,
    checklists: records,
    total: totalCount,
    filteredTotal: filteredCount,
    filters,
    sortableColumns,
    isLoading: false,
  };
}

function onLoadChecklistsError(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isLoading: false,
  };
}

function onCreateChecklist(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isCreating: true,
  };
}

function onCreateChecklistSuccess(state: ChecklistsState, { payload }: CreateChecklistSuccessAction): ChecklistsState {
  return {
    ...state,
    isCreating: false,
    createdChecklist: payload.checklist,
  };
}

function onCreateChecklistError(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isCreating: false,
  };
}

function onUpdateChecklist(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isUpdating: true,
  };
}

function onUpdateChecklistSuccess(state: ChecklistsState, { payload }: UpdateChecklistSuccessAction): ChecklistsState {
  return {
    ...state,
    isUpdating: false,
    updatedChecklist: payload.checklist,
  };
}

function onUpdateChecklistError(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isUpdating: false,
  };
}

function onLoadChecklist(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isChecklistLoading: true,
    isChecklistLoaded: false,
    loadedChecklist: null,
  };
}

function onLoadChecklistSuccess(state: ChecklistsState, { payload }: LoadChecklistSuccessAction): ChecklistsState {
  return {
    ...state,
    loadedChecklist: payload.checklist,
    isChecklistLoading: false,
    isChecklistLoaded: true,
  };
}

function onLoadChecklistError(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isChecklistLoading: false,
    isChecklistLoaded: true,
  };
}

function onLoadFormData(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isFormDataLoading: true,
  };
}

function onLoadFormDataSuccess(state: ChecklistsState, { payload }: LoadFormDataSuccessAction): ChecklistsState {
  return {
    ...state,
    formData: { legalEntities: payload.legalEntities, predefinedTasks: payload.predefinedTasks },
    isFormDataLoading: false,
    isFormDataLoaded: true,
  };
}

function onLoadFormDataError(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isFormDataLoading: false,
    isFormDataLoaded: true,
  };
}

function onLoadPositions(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isPositionsLoading: true,
  };
}

function onLoadPositionsSuccess(state: ChecklistsState, { payload }: LoadPositionsSuccessAction): ChecklistsState {
  return {
    ...state,
    positions: payload.positions,
    isPositionsLoading: false,
    isPositionsLoaded: true,
  };
}

function onLoadPositionsError(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isPositionsLoading: false,
    isPositionsLoaded: true,
  };
}

function onDeleteChecklist(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isDeleting: true,
  };
}

function onDeleteChecklistSettled(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isDeleting: false,
  };
}

function onLoadUnits(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isUnitsLoading: true,
  };
}

function onLoadUnitsSuccess(state: ChecklistsState, { payload }: LoadUnitsSuccessAction): ChecklistsState {
  return {
    ...state,
    units: payload.units,
    isUnitsLoading: false,
    isUnitsLoaded: true,
  };
}

function onLoadUnitsError(state: ChecklistsState): ChecklistsState {
  return {
    ...state,
    isUnitsLoading: false,
    isUnitsLoaded: true,
  };
}

function onValidateUnitsSuccess(state: ChecklistsState, { payload }: ValidateUnitsSuccessAction): ChecklistsState {
  return {
    ...state,
    invalidUnitIds: payload.invalidIds,
  };
}

export default createReducer(initState, {
  [actionTypes.LOAD_CHECKLISTS]: onLoadChecklists,
  [actionTypes.LOAD_CHECKLISTS_SUCCESS]: onLoadChecklistsSuccess,
  [actionTypes.LOAD_CHECKLISTS_ERROR]: onLoadChecklistsError,
  [actionTypes.CREATE_CHECKLIST_REQUEST]: onCreateChecklist,
  [actionTypes.CREATE_CHECKLIST_SUCCESS]: onCreateChecklistSuccess,
  [actionTypes.CREATE_CHECKLIST_ERROR]: onCreateChecklistError,
  [actionTypes.UPDATE_CHECKLIST_REQUEST]: onUpdateChecklist,
  [actionTypes.UPDATE_CHECKLIST_SUCCESS]: onUpdateChecklistSuccess,
  [actionTypes.UPDATE_CHECKLIST_ERROR]: onUpdateChecklistError,
  [actionTypes.LOAD_CHECKLIST_REQUEST]: onLoadChecklist,
  [actionTypes.LOAD_CHECKLIST_SUCCESS]: onLoadChecklistSuccess,
  [actionTypes.LOAD_CHECKLIST_ERROR]: onLoadChecklistError,
  [actionTypes.LOAD_FORM_DATA_REQUEST]: onLoadFormData,
  [actionTypes.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actionTypes.LOAD_FORM_DATA_ERROR]: onLoadFormDataError,
  [actionTypes.LOAD_POSITIONS_REQUEST]: onLoadPositions,
  [actionTypes.LOAD_POSITIONS_SUCCESS]: onLoadPositionsSuccess,
  [actionTypes.LOAD_POSITIONS_ERROR]: onLoadPositionsError,
  [actionTypes.DELETE_CHECKLIST]: onDeleteChecklist,
  [actionTypes.DELETE_CHECKLIST_SUCCESS]: onDeleteChecklistSettled,
  [actionTypes.DELETE_CHECKLIST_ERROR]: onDeleteChecklistSettled,
  [actionTypes.LOAD_UNITS_REQUEST]: onLoadUnits,
  [actionTypes.LOAD_UNITS_SUCCESS]: onLoadUnitsSuccess,
  [actionTypes.LOAD_UNITS_ERROR]: onLoadUnitsError,
  [actionTypes.VALIDATE_UNITS_SUCCESS]: onValidateUnitsSuccess,
});
